import { normalizeApiError } from '../api/apiError';
import type { AttendanceActionResult } from '../services/attendanceService';
import type { TodayAttendance } from '../types/dashboard';
import { isAttendanceCheckedOut } from './totalHours';

export type PunchKind = 'in' | 'out' | 'breakOut' | 'breakIn';

/**
 * Ambiguous transport failures and "already done" responses where the server
 * may already have saved the punch (timeout after commit, duplicate submit).
 */
const RECOVERABLE_CODES = new Set([
  'REQUEST_TIMEOUT',
  'NETWORK_ERROR',
  'ATTENDANCE_ALREADY_CHECKED_IN',
  'FACE_VERIFICATION_ALREADY_USED',
  'ATTENDANCE_ALREADY_COMPLETE',
  'BREAK_ALREADY_STARTED',
  'BREAK_NOT_STARTED',
]);

export function isRecoverablePunchError(error: unknown): boolean {
  const code = normalizeApiError(error).code;
  return Boolean(code && RECOVERABLE_CODES.has(code));
}

function resultFromToday(
  today: TodayAttendance,
  kind: PunchKind,
): AttendanceActionResult | null {
  if (kind === 'in') {
    if (!today.checkInTime) return null;
    // Only recover when check-in is no longer offered (already recorded).
    if (today.canCheckIn === true) return null;
    return {
      eventTime: today.checkInTime,
      localEventTime: today.checkInTime,
      checkInTime: today.checkInTime,
      breakMinutes: today.breakMinutes,
      workedMinutes: today.workedMinutes,
    };
  }
  if (kind === 'out') {
    if (!isAttendanceCheckedOut(today) && !today.checkOutTime) return null;
    if (!today.checkOutTime && today.canCheckOut === true) return null;
    return {
      eventTime: today.checkOutTime,
      localEventTime: today.checkOutTime,
      checkInTime: today.checkInTime,
      breakMinutes: today.breakMinutes,
      workedMinutes: today.workedMinutes,
    };
  }
  if (kind === 'breakOut') {
    if (!today.isOnBreak && !today.openBreakStartedAt) return null;
    return {
      eventTime: today.openBreakStartedAt,
      localEventTime: today.openBreakStartedAt,
      checkInTime: today.checkInTime,
      breakMinutes: today.breakMinutes,
      workedMinutes: today.workedMinutes,
    };
  }
  // breakIn: recover when no longer on break and a break total exists (or end-break not offered).
  if (today.isOnBreak) return null;
  if (today.canEndBreak === true) return null;
  if (!today.hasCompletedBreak && (today.breakMinutes == null || today.breakMinutes <= 0)) {
    return null;
  }
  return {
    eventTime: null,
    localEventTime: null,
    checkInTime: today.checkInTime,
    breakMinutes: today.breakMinutes,
    workedMinutes: today.workedMinutes,
  };
}

/**
 * After an ambiguous punch failure, re-read today. If the attendance state
 * already reflects the intended action, treat the operation as successful.
 */
export function recoverPunchFromToday(
  kind: PunchKind,
  today: TodayAttendance | null | undefined,
  error: unknown,
): AttendanceActionResult | null {
  if (!today || !isRecoverablePunchError(error)) return null;
  return resultFromToday(today, kind);
}
